// Unified Supabase client with environment variable support
import { createClient } from '@supabase/supabase-js';
import { secureLog } from '../utils/secureLogging';
import { getCapacitorAuthStorage } from '../utils/authStorage';

// Get Supabase credentials from environment variables
let supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
let supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// SECURITY: Only allow GRETA_ENV fallback in development
if ((!supabaseUrl || !supabaseAnonKey) && typeof window !== 'undefined' && window.GRETA_ENV && import.meta.env.MODE !== 'production') {
  console.warn('[Supabase] Using GRETA_ENV fallback values (development only)');
  supabaseUrl = window.GRETA_ENV.SUPABASE_URL || supabaseUrl;
  supabaseAnonKey = window.GRETA_ENV.SUPABASE_ANON_KEY || supabaseAnonKey;
}

// SECURITY: In production, require environment variables - no hardcoded fallbacks
if (import.meta.env.MODE === 'production') {
  if (!supabaseUrl || !supabaseAnonKey) {
    console.error('❌ CRITICAL: Supabase credentials must be set via environment variables in production');
    throw new Error('Supabase configuration missing. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY environment variables.');
  }
} else {
  // Development fallback only
  if (!supabaseUrl || !supabaseAnonKey) {
    console.warn('⚠️ Supabase credentials not set. Some features may not work.');
    console.warn('Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY environment variables.');
  }
}

// MOBILE FIX: Create singleton Supabase client with optimized configuration
// On Capacitor we use custom storage so Supabase never clears Preferences (only explicit logout does).
let supabaseInstance = null;

// Use platform check — Android uses https://localhost, so protocol-only check fails
const isCapacitor = typeof window !== 'undefined' && (
  (window.Capacitor?.getPlatform && ['ios', 'android'].includes(window.Capacitor.getPlatform())) ||
  (window.location?.protocol && window.location.protocol !== 'http:' && window.location.protocol !== 'https:')
);
const storage = isCapacitor ? getCapacitorAuthStorage() : (typeof window !== 'undefined' ? window.localStorage : undefined);

if (!supabaseInstance) {
  supabaseInstance = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      // Must stay false on web and native: if true, Supabase auto-exchanges ?code= on load and
      // races AuthCallback (and ResetPassword), consuming the PKCE verifier so the app’s
      // exchangeCodeForSession fails with "code verifier should be non-empty" / flow_state_not_found.
      detectSessionInUrl: false,
      storageKey: 'trackio-auth-token',
      storage: storage || (typeof window !== 'undefined' ? window.localStorage : undefined),
      flowType: 'pkce',
      debug: false,
      autoLoadSession: true
    },
    global: {
      headers: {
        'x-client-info': 'trackio-mobile'
      }
    },
  });

  // CapacitorHttp is enabled so fetch() uses the native stack (WebView requests to Supabase otherwise often fail from custom URL schemes).
  // CapacitorHttp does not proxy WebSockets; a broken realtime socket can spam SIGNED_OUT. Disconnect realtime on mobile — the app uses REST only.
  if (isCapacitor) {
    try {
      supabaseInstance.realtime.disconnect();
    } catch (_) {
      // ignore if realtime not yet initialised
    }
  }
  // Do NOT call setSession() on load - it can trigger refresh and, on failure, clear storage.
  // Hydrate has already put the session in localStorage; custom storage (Capacitor) keeps Preferences intact when Supabase clears.
}

export const supabase = supabaseInstance;

// Test connection on initialization (non-blocking)
supabase
  .from('users_tb2k4x9p1m')
  .select('count')
  .limit(1)
  .then(() => {
    secureLog.debug('✅ Supabase connected successfully');
  })
  .catch(() => {
    secureLog.debug('✅ Supabase connection OK (test query may fail)');
  });

export default supabase;